<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useMapGetter } from 'dashboard/composables/store';
import { messageTimestampBR } from 'shared/helpers/timeHelper';
import CampaignsAPI from 'dashboard/api/campaigns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabels.vue';

const props = defineProps({
  campaignId: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  audienceLabels: {
    type: Array,
    default: () => [],
  },
  responseMessageText: {
    type: String,
    default: null,
  },
  // For testing/stories where store is not available
  accountLabels: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const isActiveToDelete = computed(() => props.status !== STATUS_COMPLETED);

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const accountLabelsRef = useMapGetter('labels/getLabels');

const activeAccountLabels = computed(
  () => props.accountLabels || accountLabelsRef.value || []
);

const activeAudienceLabels = computed(() => {
  if (!props.audienceLabels || props.audienceLabels.length === 0) {
    return [];
  }
  const labelSet = new Set(props.audienceLabels);
  return activeAccountLabels.value.filter(({ title }) => labelSet.has(title));
});

const hasAudienceLabels = computed(() => {
  return props.audienceLabels && props.audienceLabels.length > 0;
});

const responseMessageDisplay = computed(() => {
  return props.responseMessageText || '-';
});

const isCardExpanded = ref(false);
const stats = ref(null);
const loadingStats = ref(false);

// Função para carregar/recarregar estatísticas
const loadStats = async () => {
  if (props.isLiveChatType) return;
  
  console.log('[CampaignCard] Carregando estatísticas para campanha', props.campaignId);
  loadingStats.value = true;
  try {
    const { data } = await CampaignsAPI.getCampaignStats(props.campaignId);
    console.log('[CampaignCard] Estatísticas carregadas:', data);
    stats.value = data;
  } catch (error) {
    console.error('[CampaignCard] Erro ao carregar estatísticas:', error);
  } finally {
    loadingStats.value = false;
  }
};

// Carregar estatísticas quando o card for expandido
// Para campanhas WhatsApp/SMS (não LiveChat), mostra estatísticas tanto de agendadas quanto concluídas
watch(isCardExpanded, async (expanded) => {
  console.log('[CampaignCard] Card expandido:', {
    expanded,
    hasStats: !!stats.value,
    isLiveChatType: props.isLiveChatType,
    status: props.status,
    campaignId: props.campaignId
  });
  
  if (expanded && !stats.value) {
    loadStats();
  }
});
</script>

<template>
  <CardLayout
    layout="col"
    class="cursor-pointer transition-all hover:shadow-lg"
    @click="isCardExpanded = !isCardExpanded"
  >
    <!-- Wrapper Responsivo: Coluna no Mobile, Linha no Desktop (>= 550px) -->
    <div class="flex flex-col w-full gap-4 min-[550px]:flex-row min-[550px]:items-center min-[550px]:justify-between">
      <!-- Título (Mobile: Row justify-between; Desktop: Default) -->
      <div class="flex items-center justify-between w-full gap-2 min-[550px]:w-auto min-[550px]:justify-start min-[550px]:flex-1 overflow-hidden">
        <span
          class="text-base font-semibold text-n-slate-12 truncate flex-1 min-w-0"
        >
          {{ title }}
        </span>
        <!-- Mobile Audience Labels (Right aligned on mobile via justify-between) -->
        <div
          v-if="!isLiveChatType && hasAudienceLabels"
          class="min-[550px]:hidden flex-shrink-0"
        >
          <CardLabels
            :conversation-labels="props.audienceLabels"
            :account-labels="activeAccountLabels"
          />
        </div>
      </div>

      <!-- Coluna Direita: Info e Ações (Mobile: Left Aligned; Desktop: Right Aligned) -->
      <div class="flex items-center justify-start w-full gap-3 min-[550px]:w-auto min-[550px]:justify-end flex-shrink-0 min-[550px]:self-start">
        <!-- Desktop Audience Labels -->
        <div v-if="!isLiveChatType && hasAudienceLabels" class="hidden min-[550px]:flex justify-end flex-shrink-0">
          <CardLabels
            :conversation-labels="props.audienceLabels"
            :account-labels="activeAccountLabels"
          />
        </div>

        <!-- Data Agendada/Enviada -->
        <span
          v-if="scheduledAt"
          class="text-xs font-medium text-n-slate-11 whitespace-nowrap"
        >
          {{ messageTimestampBR(scheduledAt, 'dd/MM/yy HH:mm') }}
        </span>

        <!-- Status -->
        <span
          class="text-xs font-medium inline-flex items-center px-2.5 py-1.5 rounded-md bg-n-alpha-2 flex-shrink-0 leading-none"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>

        <!-- Botões de ação -->
        <div class="flex items-center gap-2" @click.stop>
          <Button
            v-if="isLiveChatType"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-sliders-vertical"
            @click="emit('edit')"
          />
          <Button
            v-if="isActiveToDelete"
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash"
            @click="emit('delete')"
          />
        </div>
      </div>
    </div>

    <!-- Conteúdo Expandível (Abaixo da linha principal) -->
    <template #after>
      <div
        class="grid transition-[grid-template-rows] duration-300 ease-in-out w-full"
        :class="isCardExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-4 px-6 pb-5">
            <!-- Mensagem da campanha -->
            <div
              v-dompurify-html="formatMessage(message, false, false, false)"
              class="text-sm text-n-slate-11 [&>p]:mb-0 min-h-[2.5rem]"
            />

            <!-- Informações adicionais -->
            <div v-if="!isLiveChatType && responseMessageText && responseMessageText.trim()" class="flex flex-col gap-3 w-full">
              <!-- Resposta automática -->
              <div class="flex flex-col gap-1.5 pl-3 border-l-2 border-n-strong">
                <div class="flex items-center gap-1.5 text-n-slate-10">
                  <span class="i-lucide-corner-down-right size-3.5" />
                  <span class="text-xs font-medium">
                    {{ t('CAMPAIGN.CARD.RESPONSE_MESSAGE_LABEL', 'Resposta automática') }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11">
                  {{ responseMessageDisplay }}
                </p>
              </div>
            </div>

            <!-- Estatísticas de Envio -->
            <div v-if="stats && !isLiveChatType" class="flex flex-col gap-3 border-t border-n-weak pt-4">
              <!-- Cabeçalho com botão de refresh para campanhas ativas -->
              <div v-if="status !== STATUS_COMPLETED" class="flex items-center justify-between">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.CARD.STATS.TITLE') }}
                </span>
                <button
                  type="button"
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded transition-colors"
                  :disabled="loadingStats"
                  @click.stop="loadStats"
                >
                  <span class="i-lucide-refresh-cw size-3" :class="{ 'animate-spin': loadingStats }" />
                  <span>{{ t('CAMPAIGN.CARD.STATS.REFRESH') }}</span>
                </button>
              </div>
              
              <!-- Contadores -->
              <div class="flex gap-4">
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-n-slate-11">
                    {{ t('CAMPAIGN.CARD.STATS.TOTAL') }}
                  </span>
                  <span class="text-lg font-semibold text-n-slate-12">
                    {{ stats.total_count }}
                  </span>
                </div>
                
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-n-teal-11">
                    {{ t('CAMPAIGN.CARD.STATS.SUCCESS') }}
                  </span>
                  <span class="text-lg font-semibold text-n-teal-11">
                    {{ stats.success_count }}
                  </span>
                </div>
                
                <div class="flex flex-col gap-1">
                  <span class="text-xs text-n-ruby-11">
                    {{ t('CAMPAIGN.CARD.STATS.ERROR') }}
                  </span>
                  <span class="text-lg font-semibold text-n-ruby-11">
                    {{ stats.error_count }}
                  </span>
                </div>
              </div>
            </div>

            <div v-else-if="loadingStats" class="border-t border-n-weak pt-4">
              <span class="text-xs text-n-slate-11">
                {{ t('CAMPAIGN.CARD.STATS.LOADING') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </CardLayout>
</template>
